import type { TaxRateLike } from '@/utils/nontaxDiffTooltip';
import type { CompanyInsuranceParts } from '@/utils/nontaxBeforeCompanyIns';
import type { FacilityWageDeductionSource } from '@/utils/nontaxBeforeDeduction';

export const NONTAX_ONH_MONTHLY_LIMIT = 200000;

const nc = (value: string | number | null | undefined): number => {
  if (value == null) return 0;
  return parseFloat(value.toString().replace(/,/g, '')) || 0;
};

const oneDel = (value: number): number =>
  Math.ceil(parseFloat(String(value || 0).toString().replace(/,/g, '')) / 10) * 10;

const fixRound10 = (value: number): number => Math.floor((Number(value) || 0) / 10) * 10;

const getDepe1 = (
  value: number,
  getSo: Array<{ income1: number; income2: number; depe_1: number }>,
): number => {
  for (const obj of getSo || []) {
    const income1 = parseFloat(String(obj.income1));
    const income2 = parseFloat(String(obj.income2));
    if (value >= income1 && value < income2) {
      return parseFloat(String(obj.depe_1)) || 0;
    }
  }
  return 0;
};

export interface NontaxImprovementInput {
  uploadLunch: number;
  improvedLunch: number;
  onhOvertime: number;
  onhNight: number;
  onhHoliday: number;
  applyOnhImprovement: boolean;
}

export interface NontaxImprovementBreakdown {
  uploadLunch: number;
  improvedLunch: number;
  lunchImprovement: number;
  onhGross: number;
  onhImprovement: number;
  totalImprovement: number;
}

export interface InsuranceFlags {
  hasHealth: boolean;
  hasLongTerm: boolean;
  hasEmployment: boolean;
  isBoss: boolean;
}

export interface MarginalSavingParts {
  employerGun: number;
  employerJang: number;
  employerGo: number;
  employerSil: number;
  employerSan: number;
  employeeGun: number;
  employeeJang: number;
  employeeGo: number;
  employeeSo: number;
  employeeJi: number;
  retirement: number;
}

export interface MarginalSavingBreakdown {
  parts: MarginalSavingParts;
  employerTotal: number;
  employeeTotal: number;
  retirement: number;
  total: number;
}

export const calcNontaxImprovement = (input: NontaxImprovementInput): NontaxImprovementBreakdown => {
  const uploadLunch = Math.max(0, input.uploadLunch);
  const improvedLunch = Math.max(0, input.improvedLunch);
  const lunchImprovement = Math.max(0, improvedLunch - uploadLunch);
  const onhGross =
    Math.max(0, input.onhOvertime) + Math.max(0, input.onhNight) + Math.max(0, input.onhHoliday);
  const onhImprovement = input.applyOnhImprovement
    ? Math.min(onhGross, NONTAX_ONH_MONTHLY_LIMIT)
    : 0;
  const totalImprovement = lunchImprovement + onhImprovement;

  return {
    uploadLunch,
    improvedLunch,
    lunchImprovement,
    onhGross,
    onhImprovement,
    totalImprovement,
  };
};

export const inferFacilityInsuranceFlags = (wage: FacilityWageDeductionSource): InsuranceFlags => {
  const isBoss = nc(wage.cwhd_is_owner) === 1;
  return {
    hasHealth: nc(wage.cwhd_l_gun) > 0,
    hasLongTerm: nc(wage.cwhd_l_jang) > 0,
    hasEmployment: !isBoss && nc(wage.cwhd_l_go) > 0,
    isBoss,
  };
};

export const inferDiagnosisInsuranceFlags = (row: {
  empInsu?: string | number[] | null;
  boss?: string | number | boolean | null;
  l_gun?: number | null;
  l_jang?: number | null;
  l_go?: number | null;
}): InsuranceFlags => {
  const insu: number[] = [];
  if (Array.isArray(row.empInsu)) {
    insu.push(...row.empInsu);
  } else if (typeof row.empInsu === 'string') {
    try {
      const parsed = JSON.parse(row.empInsu.replace(/'/g, '"'));
      if (Array.isArray(parsed)) insu.push(...parsed);
    } catch {
      // ignore
    }
  }
  const isBoss = row.boss === '1' || row.boss === 1 || row.boss === true;
  return {
    hasHealth: insu.includes(2) || nc(row.l_gun) > 0,
    hasLongTerm: insu.includes(3) || nc(row.l_jang) > 0,
    hasEmployment: !isBoss && (insu.includes(4) || nc(row.l_go) > 0),
    isBoss,
  };
};

const calcMarginalEmployerSaving = (
  taxableReduction: number,
  tax: TaxRateLike,
  after: CompanyInsuranceParts,
  isBoss: boolean,
): Pick<
  MarginalSavingParts,
  'employerGun' | 'employerJang' | 'employerGo' | 'employerSil' | 'employerSan'
> => {
  const D = Math.max(0, taxableReduction);
  const parts = {
    employerGun: 0,
    employerJang: 0,
    employerGo: 0,
    employerSil: 0,
    employerSan: 0,
  };

  if (D <= 0) return parts;

  if (after.c_gun > 0) {
    parts.employerGun = fixRound10(D * tax.tr_gun);
    if (after.c_jang > 0) {
      parts.employerJang = fixRound10(parts.employerGun * tax.tr_jang);
    }
  }

  if (!isBoss && after.c_go > 0) {
    parts.employerGo = fixRound10(D * tax.tr_go);
  }

  if (!isBoss && after.c_sil > 0) {
    parts.employerSil = fixRound10(D * tax.tr_sil);
  }

  if (!isBoss && after.c_san > 0) {
    parts.employerSan = fixRound10(D * tax.tr_san);
  }

  return parts;
};

const calcMarginalEmployeeSaving = (
  taxableReduction: number,
  afterTaxable: number,
  tax: TaxRateLike,
  getSo: Array<{ income1: number; income2: number; depe_1: number }>,
  flags: InsuranceFlags,
): Pick<MarginalSavingParts, 'employeeGun' | 'employeeJang' | 'employeeGo' | 'employeeSo' | 'employeeJi'> => {
  const D = Math.max(0, taxableReduction);
  const parts = {
    employeeGun: 0,
    employeeJang: 0,
    employeeGo: 0,
    employeeSo: 0,
    employeeJi: 0,
  };

  if (D <= 0) return parts;

  if (flags.hasHealth) {
    parts.employeeGun = oneDel(D * tax.tr_gun);
    if (flags.hasLongTerm && parts.employeeGun > 0) {
      parts.employeeJang = oneDel(parts.employeeGun * tax.tr_jang);
    }
  }

  if (!flags.isBoss && flags.hasEmployment) {
    parts.employeeGo = oneDel(D * tax.tr_go);
  }

  if (getSo.length) {
    const taxableBefore = afterTaxable + D;
    const soBefore = getDepe1(taxableBefore / 1000, getSo);
    const soAfter = getDepe1(afterTaxable / 1000, getSo);
    parts.employeeSo = Math.max(0, soBefore - soAfter);
    parts.employeeJi = Math.max(0, oneDel(soBefore / 10) - oneDel(soAfter / 10));
  }

  return parts;
};

export const calcMarginalSavingOnImprovement = ({
  improvement,
  afterTaxable,
  tax,
  getSo,
  flags,
  afterCompanyIns,
  afterRetirement,
}: {
  improvement: NontaxImprovementBreakdown;
  afterTaxable: number;
  tax: TaxRateLike;
  getSo: Array<{ income1: number; income2: number; depe_1: number }>;
  flags: InsuranceFlags;
  afterCompanyIns: CompanyInsuranceParts;
  afterRetirement: number;
}): MarginalSavingBreakdown => {
  const D = improvement.totalImprovement;
  const employerParts = calcMarginalEmployerSaving(D, tax, afterCompanyIns, flags.isBoss);
  const employeeParts = calcMarginalEmployeeSaving(D, afterTaxable, tax, getSo, flags);
  const retirement =
    afterRetirement > 0 && improvement.lunchImprovement > 0
      ? fixRound10(improvement.lunchImprovement / 12)
      : 0;

  const parts: MarginalSavingParts = {
    ...employerParts,
    ...employeeParts,
    retirement,
  };

  const employerTotal =
    parts.employerGun +
    parts.employerJang +
    parts.employerGo +
    parts.employerSil +
    parts.employerSan;
  const employeeTotal =
    parts.employeeGun +
    parts.employeeJang +
    parts.employeeGo +
    parts.employeeSo +
    parts.employeeJi;
  const total = employerTotal + employeeTotal + retirement;

  return {
    parts,
    employerTotal,
    employeeTotal,
    retirement,
    total,
  };
};
